// Shared owner/person lookup for the Agents app (AMB-341 / B2; tenant-aware AMB-498).
//
// Owner values arrive from records as EITHER a slug ("claire") OR a display name
// ("Claire", "Roxanne", "Kyle / Ambid"): generation writes capitalized first names
// while in-app edits persist slugs. The lookup must normalize (trim + lowercase) so a
// team member always resolves to their roster avatar, and a non-team owner still shows
// their real name + clean initials — never a lowercase slug and never a false
// "Unassigned". This is the one canonical version (TranscriptView + KnowledgeBase had
// their own un-normalized copies that showed "Unassigned"/raw slugs); Digest's
// personFor is the same logic this mirrors.
//
// AMB-498: the roster is read from getActiveRoster() at CALL time, not baked in at module
// load. So once the signed-in tenant's members load (roster.ts loadTenantRoster), an outside
// agency's owner avatars resolve to THAT agency's people — never the built-in Ambid list.
// Ambid (no operator roster) falls back to the built-in ROSTER unchanged.

import { getActiveRoster } from "./roster";

export interface Person {
  name: string;
  initials: string;
  color: string;
}

// The one non-human owner deliverables can carry; not a roster member, so it's matched here.
const COPYWRITER_AGENT: Person = { name: "Copywriter Agent", initials: "CA", color: "bg-blue-500" };

const norm = (s?: string | null): string => (s || "").trim().toLowerCase();

// Resolve any owner value to a Person against the ACTIVE tenant roster. Team member (matched
// by id OR display name, normalized) → roster avatar; the copywriter agent → its fixed avatar;
// otherwise derive name + initials from the raw value; empty → "Unassigned".
export const personFor = (raw?: string | null): Person => {
  const key = norm(raw);
  if (key === "copywriter-agent") return COPYWRITER_AGENT;
  if (key) {
    const m = getActiveRoster().find((r) => norm(r.id) === key || norm(r.name) === key);
    if (m) return { name: m.name, initials: m.initials, color: m.color };
  }
  const name = (raw || "").trim();
  const initials =
    name
      .split(/[\s/]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?";
  return { name: name || "Unassigned", initials, color: "bg-gray-400" };
};

export const personName = (raw?: string | null): string => personFor(raw).name;
export const personColor = (raw?: string | null): string => personFor(raw).color;
export const personInitials = (raw?: string | null): string => personFor(raw).initials;
